import React, { useEffect, useRef, useState } from 'react';
import { FormControl, FormHelperText, Icon, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import DialogBase from './DialogBase';
import { Organisatie } from '../../../models/organisatie/organisatie';
import { Beoordeling } from '../../../models/plan/beoordeling';
import { plannenService } from '../../../services/plannen.service';

interface DialogBeoordelingAddProps {
  open: boolean;
  organisatie?: Organisatie;
  planNaam?: string;
  onClose: () => void;
  onSubmit: (beoordeling: any) => void;
}

const MAX_LENGTH = 2000;
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
};

const DialogBeoordelingAdd: React.FC<DialogBeoordelingAddProps> = ({
  open,
  organisatie,
  planNaam,
  onClose,
  onSubmit
}) => {
  const [beoordeling, setBeoordeling] = useState<Beoordeling>({} as Beoordeling);
  const [beoordelingStatusOptions, setStatusses] = useState<any[]>([]);
  const [charCount, setCharCount] = useState(beoordeling?.remark?.length || 0);

  const [validationErrors, setValidationErrors] = useState<{ 
    status?: string; 
    remark?: string;
  }>({});

  const hasStatussesData = useRef(false);

  
  useEffect(() => {
    // Ensure the fetch happens only once
    if (!hasStatussesData.current) {
      getBeoordelingStatusses();
      hasStatussesData.current = true; // Mark as fetched
    }
  }, []);

  useEffect(() => {
    if (open) {
      // Nieuw openen → reset alles
      setBeoordeling({} as Beoordeling);
      setValidationErrors({});
    }
  }, [open]);
  

  const getBeoordelingStatusses = async () => {
    await plannenService.getBeoordelingStatusses();
    const statusses = plannenService.beoordelingStatusses as any[];
    setStatusses(statusses);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Create a copy of the state and update the displayName
    const updatedBeoordeling = {
      ...beoordeling, // Copy the current state
      remark: event.target.value, // Update displayName
    };

    setCharCount(event.target.value.length);

    // Update the state to trigger re-render
    setBeoordeling(updatedBeoordeling);
  };

  const onChangeStatus = (event: any) => {
    setValidationErrors(prev => ({ ...prev, status: undefined }));
    // Create a copy of the state and update the displayName
    const updatedBeoordeling = {
      ...beoordeling, // Copy the current state
      key: event.target.value, // Update displayName
    };

    // Update the state to trigger re-render
    setBeoordeling(updatedBeoordeling);
  }

  const handleSubmit = () => {
    const errors: { status?: string; remark?: string } = {};

    if (!beoordeling.key) {
      errors.status = 'Status is verplicht';
    }

    if (beoordeling.remark && beoordeling.remark.length > 2000) {
      errors.remark = 'Opmerking mag maximaal 2000 tekens bevatten';
    }

    // if (!beoordeling.remark || beoordeling.remark.trim() === '') {
    //   errors.remark = 'Opmerking is verplicht';
    // }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    onSubmit(beoordeling);
    onClose(); // Close the dialog after submission
  };

  return (
    <DialogBase
      open={open}
      title={`Beoordelingen toevoegen${planNaam ? ` voor ${planNaam}` : ''}`}
      contentText=""
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Opslaan"
      confirmButtonIcon={<Icon>save</Icon>}
    >
      <section>
        <label htmlFor="select-status">Status</label>
        <br/>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <InputLabel id="select-status-label">Kies een status</InputLabel>
          <Select
            id="select-status"
            labelId="select-status-label"
            autoWidth
            variant="filled"
            disabled={!(beoordelingStatusOptions.length > 0)}
            onChange={ onChangeStatus }
            value={beoordeling?.key ? String(beoordeling.key) : ""}
            error={!!validationErrors.status}
            MenuProps={MenuProps}
            >
            <MenuItem value="">
              <em>&nbsp;</em>
            </MenuItem>
            {beoordelingStatusOptions.map((option) => (
              <MenuItem key={option.key} value={String(option.key)}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
          {validationErrors.status && (
            <span style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: '4px' }}>
              {validationErrors.status}
            </span>
          )}
        </FormControl>
      </section>

      <section>
        <label htmlFor="remark">Opmerking</label>
        <br/>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <TextField
            fullWidth
            id="remark"
            label="Vul een opmerking in"
            variant="filled"
            multiline
            rows={5}
            // maxRows={10}
            onChange={handleInputChange}
            value={beoordeling?.remark ? String(beoordeling.remark) : ""}
            error={!!validationErrors.remark}
            helperText={validationErrors.remark}
            inputProps={{ maxLength: MAX_LENGTH }}
          />
          <FormHelperText
            sx={{
              textAlign: 'right',
              fontSize: '0.8rem',
              color: charCount >= MAX_LENGTH ? 'error.main' : 'text.secondary',
              marginRight: 1,
            }}
          >
            {charCount} / {MAX_LENGTH}
          </FormHelperText>
          {/* {validationErrors.remark && (
            <span style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: '4px' }}>
              {validationErrors.remark}
            </span>
          )} */}
        </FormControl>
      </section>
      
    </DialogBase>
  );
};

export default DialogBeoordelingAdd;
